import React from 'react';
import { Box, Chip } from '@mui/material';
import { formatCurrency } from '../../utils/formatCurrency';
import { creditedViaText } from '../../utils/creditedVia';
import WorkshopLink, { WorkshopLinks, workshopNames } from '../WorkshopLink';
import { fmtWinRate, winRateInline } from '../../utils/winRate';
import { formatShortDate } from '../../utils/formatTime';
import { dealNameColumn, workshopCountColumn } from './DealLink';
import { PersonDealsCell, personDealsCoverage, dealsLabel } from './PersonDeals';
import { PERSON_DEALS_LABEL } from './personDealsText';

const fmtCurrency = (v) => formatCurrency(v);

const fmtPercent = fmtWinRate;

const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return v; }
};

export const OPPORTUNITY_COLUMNS = [
  { id: 'oppname',      label: 'Opportunity',  width: 230 },
  { id: 'account_name', label: 'Account',      width: 150 },
  { id: 'oppamt',       label: 'Amount',        width: 100, align: 'right', format: fmtCurrency },
  { id: 'stage_name',   label: 'Stage',         width: 130, chipType: 'stage' },
  { id: 'created_date', label: 'Created',       width: 96,  format: fmtDate },
  { id: 'close_date',   label: 'Close Date',    width: 96,  format: fmtDate },
  { id: 'fullname',     label: 'Participant',   width: 140,
    renderCell: (v, row) => v || row?.email || '—' },
  { id: 'workshops',    label: 'Workshop',      width: 170, noWrap: false,
    format: workshopNames, renderCell: (v) => <WorkshopLinks workshops={v} /> },
  { id: 'geography',    label: 'Geo',           width: 70 },
  { id: 'region',       label: 'Region',        width: 100 },
];

export const PARTICIPANT_COLUMNS = [
  { id: 'fullname',       label: 'Name',       width: 160 },
  { id: 'email',          label: 'Email',      width: 200 },
  { id: 'geography',      label: 'Geo',        width: 70 },
  { id: 'region',         label: 'Region',     width: 120 },
  { id: 'workshop_count', label: 'Workshops',  width: 90,  align: 'right' },
  { id: 'pipeline',       label: 'Open pipeline $', width: 120, align: 'right', format: fmtCurrency, tooltip: 'Open opportunities only (won and lost are separate columns)' },
  { id: 'won',            label: 'Won $',      width: 110, align: 'right', format: fmtCurrency },
];

export const WORKSHOP_COLUMNS = [
  { id: 'name',            label: 'Workshop',      width: 220,
    renderCell: (name, row) => <WorkshopLink id={row.workshop_id} name={name} /> }, // workshop_id = workshop.id on /exec/workshops-ranked (PR B1); plain text until then
  { id: 'participants',    label: 'Participants',   width: 100, align: 'right' },
  { id: 'unique_accounts', label: 'Accounts',      width: 90,  align: 'right' },
  { id: 'pipeline',        label: 'Total opp. value $', width: 130, align: 'right', format: fmtCurrency, tooltip: 'All stages: open, won and lost' },
  { id: 'won',             label: 'Won $',         width: 120, align: 'right', format: fmtCurrency },
  { id: 'win_rate',        label: 'Win Rate',      width: 130, align: 'right', format: fmtPercent,
    renderCell: (v, row) => winRateInline(v, { n: row?.win_rate_n, openShare: row?.open_share }) },
];

const OPP_FILTER_KEYS = ['stage', 'productFamily', 'quarter', 'workshop', 'account',
                         'amountMin', 'amountMax', 'geography', 'region', 'minWorkshops', 'maxWorkshops'];

export const OPP_DEDUP_CONFIG = {
  endpoint: '/exec/opportunities',
  buildParams: (base, filters) => {
    const p = new URLSearchParams();
    if (base.startDate) p.set('startDate', base.startDate);
    if (base.endDate)   p.set('endDate', base.endDate);
    p.set('windowMonths', String(base.windowMonths ?? 18));
    p.set('postWorkshopOnly', String(base.postWorkshopOnly ?? false));
    p.set('deduplicate', 'true');
    if (base.page !== undefined)     p.set('page', String(base.page + 1));
    if (base.pageSize !== undefined) p.set('pageSize', String(base.pageSize));
    if (base.sortBy)    p.set('sortBy', base.sortBy);
    if (base.sortOrder) p.set('sortOrder', base.sortOrder);
    OPP_FILTER_KEYS.forEach(k => {
      const v = filters?.[k];
      if (v != null && v !== '') p.set(k, String(v));
    });
    return p;
  },
  columns: OPPORTUNITY_COLUMNS,
  totalKey: 'oppamt',
  idKey: 'oppid',
  panelType: 'opportunities',
};

export const PARTICIPANT_CONFIG = {
  endpoint: '/exec/employees',
  buildParams: (base, filters) => {
    const p = new URLSearchParams();
    if (base.startDate) p.set('startDate', base.startDate);
    if (base.endDate)   p.set('endDate', base.endDate);
    if (base.startDate) p.set('oppStartDate', base.startDate);
    if (base.endDate)   p.set('oppEndDate', base.endDate);
    p.set('windowMonths', '0');
    p.set('postWorkshopOnly', 'false');
    if (base.page !== undefined)     p.set('page', String(base.page + 1));
    if (base.pageSize !== undefined) p.set('pageSize', String(base.pageSize));
    if (base.sortBy)    p.set('sortBy', base.sortBy);
    if (base.sortOrder) p.set('sortOrder', base.sortOrder);
    return p;
  },
  columns: PARTICIPANT_COLUMNS,
  totalKey: 'pipeline',
  idKey: 'email',
  panelType: 'participants',
};

export const WORKSHOP_CONFIG = {
  endpoint: '/exec/workshops-ranked',
  buildParams: (base) => {
    const p = new URLSearchParams();
    if (base.startDate) p.set('startDate', base.startDate);
    if (base.endDate)   p.set('endDate', base.endDate);
    p.set('windowMonths', '0');
    p.set('postWorkshopOnly', 'false');
    return p;
  },
  columns: WORKSHOP_COLUMNS,
  totalKey: 'pipeline',
  idKey: 'name',
  panelType: 'workshops',
};

export const EXTERNAL_PARTICIPANT_COLUMNS = [
  { id: 'fullname',       label: 'Name',         width: 160 },
  { id: 'email',          label: 'Email',        width: 220 },
  { id: 'workshop_count', label: 'Workshops',    width: 90,  align: 'right' },
  { id: 'opp_count',      label: 'Opps',         width: 70,  align: 'right' },
  { id: 'pipeline',       label: 'Open pipeline $', width: 120, align: 'right', format: fmtCurrency, tooltip: 'Open opportunities only (won and lost are separate columns)' },
  { id: 'won',            label: 'Won $',        width: 110, align: 'right', format: fmtCurrency },
];

function buildSegmentParams(base, filters, participantType, excludeGeneric = true) {
  const p = new URLSearchParams();
  if (base.startDate) p.set('startDate', base.startDate);
  if (base.endDate)   p.set('endDate', base.endDate);
  p.set('windowMonths', String(base.windowMonths ?? 18));
  p.set('postWorkshopOnly', String(base.postWorkshopOnly ?? false));
  p.set('participantType', participantType);
  if (excludeGeneric) p.set('excludeGenericDomains', 'true');
  if (base.page !== undefined)     p.set('page', String(base.page + 1));
  if (base.pageSize !== undefined) p.set('pageSize', String(base.pageSize));
  if (base.sortBy)    p.set('sortBy', base.sortBy);
  if (base.sortOrder) p.set('sortOrder', base.sortOrder);
  OPP_FILTER_KEYS.forEach(k => {
    const v = filters?.[k];
    if (v != null && v !== '') p.set(k, String(v));
  });
  return p;
}

export const OPP_INTERNAL_CONFIG = {
  ...OPP_DEDUP_CONFIG,
  buildParams: (base, filters) => {
    const p = OPP_DEDUP_CONFIG.buildParams(base, filters);
    p.set('participantType', 'internal');
    return p;
  },
};

export const OPP_EXTERNAL_CONFIG = {
  ...OPP_DEDUP_CONFIG,
  buildParams: (base, filters) => buildSegmentParams(base, filters, 'external'),
};

export const PARTICIPANT_INTERNAL_CONFIG = {
  ...PARTICIPANT_CONFIG,
  buildParams: (base, filters) => {
    const p = PARTICIPANT_CONFIG.buildParams(base, filters);
    p.set('participantType', 'internal');
    return p;
  },
};

export const PARTICIPANT_EXTERNAL_CONFIG = {
  endpoint: '/exec/employees',
  buildParams: (base) => {
    const p = new URLSearchParams();
    if (base.startDate) p.set('startDate', base.startDate);
    if (base.endDate)   p.set('endDate', base.endDate);
    p.set('windowMonths', '0');
    p.set('postWorkshopOnly', 'false');
    p.set('participantType', 'external');
    p.set('excludeGenericDomains', 'true');
    if (base.page !== undefined)     p.set('page', String(base.page + 1));
    if (base.pageSize !== undefined) p.set('pageSize', String(base.pageSize));
    if (base.sortBy)    p.set('sortBy', base.sortBy);
    if (base.sortOrder) p.set('sortOrder', base.sortOrder);
    return p;
  },
  columns: EXTERNAL_PARTICIPANT_COLUMNS,
  totalKey: 'pipeline',
  idKey: 'email',
  panelType: 'participants',
};

export const WORKSHOP_INTERNAL_CONFIG = {
  ...WORKSHOP_CONFIG,
  buildParams: (base) => {
    const p = WORKSHOP_CONFIG.buildParams(base);
    p.set('participantType', 'internal');
    return p;
  },
};

export const WORKSHOP_EXTERNAL_CONFIG = {
  ...WORKSHOP_CONFIG,
  buildParams: (base) => {
    const p = WORKSHOP_CONFIG.buildParams(base);
    p.set('participantType', 'external');
    p.set('excludeGenericDomains', 'true');
    return p;
  },
};

export const OPP_CHANNEL_CONFIG = {
  ...OPP_DEDUP_CONFIG,
  buildParams: (base, filters) => {
    const p = OPP_DEDUP_CONFIG.buildParams(base, filters);
    p.set('participantType', 'external');
    p.set('accountType', 'channel');
    p.set('excludeGenericDomains', 'true');
    return p;
  },
};

export const OPP_CUSTOMER_CONFIG = {
  ...OPP_DEDUP_CONFIG,
  buildParams: (base, filters) => {
    const p = OPP_DEDUP_CONFIG.buildParams(base, filters);
    p.set('participantType', 'external');
    p.set('accountType', 'customer');
    return p;
  },
};

export const PARTICIPANT_CHANNEL_CONFIG = {
  ...PARTICIPANT_EXTERNAL_CONFIG,
  buildParams: (base) => {
    const p = PARTICIPANT_EXTERNAL_CONFIG.buildParams(base);
    p.set('accountType', 'channel');
    return p;
  },
};

export const PARTICIPANT_CUSTOMER_CONFIG = {
  ...PARTICIPANT_EXTERNAL_CONFIG,
  buildParams: (base) => {
    const p = PARTICIPANT_EXTERNAL_CONFIG.buildParams(base);
    p.set('accountType', 'customer');
    return p;
  },
};

function fmtWorkshops(v) {
  if (!v) return '—';
  const parts = v.split('||').filter(Boolean);
  const shown = parts.slice(0, 3);
  const extra = parts.length - 3;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
      {shown.map((w, i) => (
        <Chip key={i} label={w} size="small" sx={{ fontSize: '0.62rem', height: 18 }} />
      ))}
      {extra > 0 && (
        <Chip
          label={`…and ${extra} more`}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.62rem', height: 18, color: 'text.secondary' }}
        />
      )}
    </Box>
  );
}

export const TAG_DRILL_PRODUCT_COLUMNS = [
  { id: 'oppname',           label: 'Opportunity',      width: 220 },
  { id: 'account_name',      label: 'Account',          width: 150 },
  { id: 'attributed_amount', label: 'Attributed $',     width: 110, align: 'right', format: fmtCurrency,
    tooltip: 'Workshop-matched line items only (workshop product tag = SKU product tag). Contracted net price per line item (SF total_price), consistent with Salesforce Amount.' },
  { id: 'oppamt',            label: 'Deal $',           width: 100, align: 'right', format: fmtCurrency,
    tooltip: 'Full Salesforce opportunity amount' },
  { id: 'stage_name',        label: 'Stage',            width: 130, chipType: 'stage', detailKey: 'stage_detail' },
  { id: 'created_date',      label: 'Created',          width: 90,  format: fmtDate },
  { id: 'owner_name',        label: 'SF Owner',         width: 140 },
  { id: 'participant_count', label: 'Participants',     width: 90,  align: 'right',
    tooltip: 'How many SEs linked this deal via a workshop tagged with this product' },
  { id: 'workshops',         label: 'Workshops Taken',  width: 260, format: fmtWorkshops },
];

export const TAG_DRILL_OTHER_COLUMNS = [
  { id: 'oppname',           label: 'Opportunity',      width: 240 },
  { id: 'account_name',      label: 'Account',          width: 150 },
  { id: 'oppamt',            label: 'Deal $',           width: 110, align: 'right', format: fmtCurrency },
  { id: 'stage_name',        label: 'Stage',            width: 130, chipType: 'stage', detailKey: 'stage_detail' },
  { id: 'created_date',      label: 'Created',          width: 90,  format: fmtDate },
  { id: 'owner_name',        label: 'SF Owner',         width: 140 },
  { id: 'participant_count', label: 'Participants',     width: 90,  align: 'right',
    tooltip: 'How many SEs linked this deal via a workshop tagged with this pillar/competency' },
  { id: 'workshops',         label: 'Workshops Taken',  width: 280, format: fmtWorkshops },
];

export const makeTagDrillConfig = (tagName, category, attribution = 'influenced') => ({
  endpoint: '/analytics/tag-drill',
  buildParams: (base, _filters) => {
    const p = new URLSearchParams();
    p.set('tagName', tagName);
    p.set('category', category);
    if (base.startDate) p.set('startDate', base.startDate);
    if (base.endDate)   p.set('endDate', base.endDate);
    p.set('attribution', attribution);
    if (base.page !== undefined)     p.set('page', String(base.page + 1));
    if (base.pageSize !== undefined) p.set('pageSize', String(base.pageSize));
    if (base.sortBy)    p.set('sortBy', base.sortBy);
    if (base.sortOrder) p.set('sortOrder', base.sortOrder);
    return p;
  },
  columns: category === 'Product' ? TAG_DRILL_PRODUCT_COLUMNS : TAG_DRILL_OTHER_COLUMNS,
  totalKey: 'attributed_amount',
  idKey: 'oppid',
  panelType: 'opportunities',
});

// Deals shared by two pillars: the list behind an off-diagonal overlap-matrix cell (Filtered Brief only). The
// matrix and /exec/filtered/tag-analytics/pillar-overlap-opps use the same filters, so the cell count equals the
// list length (the endpoint caps at 500, sorted by Salesforce amount). "SF Deal $" is the deal's own Salesforce
// Amount, shown for context; the panel header total is a dash (amountNA): no dollar total in the overlap view.
export const OVERLAP_SHARED_COLUMNS = [
  { id: 'oppname',      label: 'Opportunity', width: 240 },
  { id: 'account_name', label: 'Account',     width: 150 },
  { id: 'oppamt',       label: 'SF Deal $',   width: 110, align: 'right', format: fmtCurrency,
    tooltip: "The deal's own Salesforce Amount, for context. Not a SKU-based or pillar-attributed figure." },
  { id: 'stage_name',   label: 'Stage',       width: 130, chipType: 'stage', detailKey: 'stage_detail' },
  { id: 'created_date', label: 'Created',     width: 90,  format: fmtDate },
  { id: 'owner_name',   label: 'SF Owner',    width: 140 },
];

export const makeOverlapSharedConfig = (pillarA, pillarB, { participantSegment = 'all', botFilter = 'exclude_bots' } = {}) => ({
  endpoint: '/exec/filtered/tag-analytics/pillar-overlap-opps',
  buildParams: (base) => {
    const p = new URLSearchParams();
    p.set('pillarA', pillarA);
    p.set('pillarB', pillarB);
    if (base.startDate) p.set('startDate', base.startDate);
    if (base.endDate)   p.set('endDate', base.endDate);
    p.set('participantSegment', participantSegment);
    p.set('bot_filter', botFilter);
    return p;
  },
  columns: OVERLAP_SHARED_COLUMNS,
  totalKey: 'oppamt',
  idKey: 'oppid',
  panelType: 'opportunities',
  amountNA: true,
  unpaged: true, // the endpoint returns the whole list (max 500) in one response
});

export const FILTERED_OPP_COLUMNS = [
  { id: 'oppname',           label: 'Opportunity',  width: 220 },
  { id: 'account_name',      label: 'Account',      width: 150 },
  { id: 'attributed_amount', label: 'Attributed $', width: 110, align: 'right', format: fmtCurrency,
    tooltip: 'Workshop-matched line items only (workshop product tag = SKU product tag). Contracted net price per line item (SF total_price), consistent with Salesforce Amount.' },
  { id: 'credited_via',      label: 'Credited via', width: 170, noWrap: false,
    format: (v) => creditedViaText(v, 'pipeline', 5) || '—',
    tooltip: 'Product tags that earned the Attributed $ (workshop drill-downs only).' },
  { id: 'oppamt',            label: 'Deal $',       width: 100, align: 'right', format: fmtCurrency,
    tooltip: 'Manually entered opportunity amount in Salesforce. Independent of line-item subtotals.' },
  { id: 'stage_name',        label: 'Stage',        width: 130, chipType: 'stage' },
  { id: 'created_date',      label: 'Created',      width: 96,  format: fmtDate },
  { id: 'owner_name',        label: 'Owner',        width: 140 },
  { id: 'workshops',         label: 'Workshop',     width: 170, noWrap: false,
    format: workshopNames, renderCell: (v) => <WorkshopLinks workshops={v} /> },
  { id: 'participant_count', label: 'Participants', width: 90,  align: 'right' },
];

const throwIfUnavailable = (d) => { if (d && d.available === false) throw new Error(d.message || `not available (${d.reason || 'unknown'})`); return d; };

// Plan 2213 group 3: `influenced` asks the route for the deal list behind the influenced figure (same rows, `basis=influenced`); the previous list
// (attendee-linked) stays for the Attendee vs Non-Attendee section. `convertDrill` flips a config when `/exec/influence/status` lists the conversion.
const FILTERED_OPP_INFLUENCED_COLUMNS = [
  dealNameColumn(FILTERED_OPP_COLUMNS[0]), FILTERED_OPP_COLUMNS[1],
  { id: 'attributed_amount', label: 'Influenced $', width: 110, align: 'right', format: fmtCurrency,
    tooltip: 'The amount the influenced figure counts for this deal: the deal lines whose product tag the attendees\' workshops carry (deep dive), or the deal amount where the figure is the whole deal.' },
  FILTERED_OPP_COLUMNS.find(c => c.id === 'oppamt'), FILTERED_OPP_COLUMNS.find(c => c.id === 'stage_name'),
  FILTERED_OPP_COLUMNS.find(c => c.id === 'created_date'), FILTERED_OPP_COLUMNS.find(c => c.id === 'owner_name'),
  workshopCountColumn(FILTERED_OPP_COLUMNS.find(c => c.id === 'workshops')),
];

export const makeFilteredOppConfig = (tagId, stage, workshopName, participantSegment = 'all', influenced = false) => ({
  endpoint: '/exec/filtered/opportunities',
  filteredOpp: [tagId, stage, workshopName, participantSegment],
  buildParams: (base) => {
    const p = new URLSearchParams();
    if (influenced) p.set('basis', 'influenced');
    if (base.startDate) p.set('startDate', base.startDate);
    if (base.endDate)   p.set('endDate', base.endDate);
    p.set('windowMonths', String(base.windowMonths ?? 18));
    if (tagId !== undefined && tagId !== null) p.set('tagId', String(tagId));
    if (stage) p.set('stage', stage);
    if (workshopName) p.set('workshopName', workshopName);
    if (participantSegment && participantSegment !== 'all') p.set('participantSegment', participantSegment);
    if (base.page !== undefined)     p.set('page', String(base.page + 1));
    if (base.pageSize !== undefined) p.set('pageSize', String(base.pageSize));
    if (base.sortBy)    p.set('sortBy', base.sortBy);
    if (base.sortOrder) p.set('sortOrder', base.sortOrder);
    return p;
  },
  columns: influenced ? FILTERED_OPP_INFLUENCED_COLUMNS : workshopName ? FILTERED_OPP_COLUMNS : FILTERED_OPP_COLUMNS.filter(c => c.id !== 'credited_via'),
  normalize: influenced ? throwIfUnavailable : undefined,
  totalKey: 'attributed_amount',
  idKey: 'oppid',
  panelType: 'opportunities',
  ...(influenced ? { noNewTab: true, maxPageSize: 100 } : {}), // a new tab would open the previous-definition list; exports page at the route's cap
});

export const PARTNER_OPP_COLUMNS = [
  { id: 'opp_name',              label: 'Opportunity',   width: 230 },
  { id: 'partner_account_name',  label: 'Partner',       width: 140 },
  { id: 'tagged_total',          label: 'Attributed $',  width: 110, align: 'right', format: fmtCurrency,
    tooltip: 'Workshop-matched line items only (workshop product tag = SKU product tag). Contracted net price per line item (SF total_price), consistent with Salesforce Amount.' },
  { id: 'opp_amount',            label: 'Deal $',        width: 100, align: 'right', format: fmtCurrency },
  { id: 'current_stage',         label: 'Stage',         width: 130, chipType: 'stage' },
  { id: 'created_date',          label: 'Created',       width: 96,  format: fmtDate },
  { id: 'owner_name',            label: 'Owner',         width: 130 },
  { id: 'matched_workshops',     label: 'Matched Workshops', width: 220, noWrap: false,
    tooltip: 'Workshops attended by this partner\'s employees whose product tag matches a tagged SKU on this deal.',
    renderCell: (v) => {
      if (!v) return '—';
      const names = v.split('||');
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
          {names.map(n => (
            <Chip key={n} label={n} size="small"
                  sx={{ fontSize: '0.6rem', height: 16, maxWidth: 180,
                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
          ))}
        </Box>
      );
    },
  },
];

export const makePartnerOppConfig = (tagId = null, stage = null) => ({
  endpoint: '/exec/filtered/partner-opportunities',
  buildParams: (base) => {
    const p = new URLSearchParams();
    if (base.startDate) p.set('startDate', base.startDate);
    if (base.endDate)   p.set('endDate', base.endDate);
    p.set('windowMonths', String(base.windowMonths ?? 18));
    if (tagId != null)  p.set('tagId', String(tagId));
    if (stage)          p.set('stage', stage);
    if (base.page !== undefined)     p.set('page', String(base.page + 1));
    if (base.pageSize !== undefined) p.set('pageSize', String(base.pageSize));
    if (base.sortBy)    p.set('sortBy', base.sortBy);
    if (base.sortOrder) p.set('sortOrder', base.sortOrder);
    return p;
  },
  columns: PARTNER_OPP_COLUMNS,
  totalKey: 'tagged_total',
  idKey: 'oppid',
  panelType: 'opportunities',
});

export const makeOverviewOppConfig = (stage = null, participantType = null, accountType = null) => ({
  endpoint: '/exec/opportunities',
  buildParams: (base) => {
    const p = new URLSearchParams();
    if (base.startDate) p.set('startDate', base.startDate);
    if (base.endDate)   p.set('endDate', base.endDate);
    p.set('windowMonths', '18');
    p.set('deduplicate', 'true');
    if (stage)           p.set('stage', stage);
    if (participantType) p.set('participantType', participantType);
    if (accountType)     p.set('accountType', accountType);
    if (base.page !== undefined)     p.set('page', String(base.page + 1));
    if (base.pageSize !== undefined) p.set('pageSize', String(base.pageSize));
    if (base.sortBy)    p.set('sortBy', base.sortBy);
    if (base.sortOrder) p.set('sortOrder', base.sortOrder);
    return p;
  },
  columns: OPPORTUNITY_COLUMNS,
  totalKey: 'oppamt',
  idKey: 'oppid',
  panelType: 'opportunities',
});

export const makeOverviewWorkshopOppConfig = (workshopName) => ({
  endpoint: '/exec/opportunities',
  buildParams: (base) => {
    const p = new URLSearchParams();
    if (base.startDate) p.set('startDate', base.startDate);
    if (base.endDate)   p.set('endDate', base.endDate);
    p.set('windowMonths', '18');
    p.set('deduplicate', 'true');
    if (workshopName)   p.set('workshop', workshopName);
    if (base.page !== undefined)     p.set('page', String(base.page + 1));
    if (base.pageSize !== undefined) p.set('pageSize', String(base.pageSize));
    if (base.sortBy)    p.set('sortBy', base.sortBy);
    if (base.sortOrder) p.set('sortOrder', base.sortOrder);
    return p;
  },
  columns: OPPORTUNITY_COLUMNS,
  totalKey: 'oppamt',
  idKey: 'oppid',
  panelType: 'opportunities',
});

export const FILTERED_BOT_CONFIG = {
  title: 'Bot & Suspicious Participants by Domain',
  columns: [
    { key: 'domain',            label: 'Domain',      width: '20%' },
    { key: 'participant_count', label: 'Participants', width: '10%', align: 'right' },
    { key: 'status',            label: 'Status',       width: '12%' },
    { key: 'bot_score',         label: 'Score',        width: '8%',  align: 'right' },
    { key: 'confidence',        label: 'Confidence',   width: '10%', align: 'right' },
    { key: 'reason',            label: 'Reason',       width: '25%' },
    { key: 'method',            label: 'Method',       width: '15%' },
  ],
  rowKey: 'domain',
  statusColorMap: { bot: '#d32f2f', suspicious: '#f57c00' },
};

// ── Influenced deal lists (plan 2213 phase 4): GET /exec/influence/deals ────────────────────────────────────────────
// ONE config for every influenced number. `of` names the number (tier:program, tag:57, category:12, workshop:123, overlap:3:4 ...,
// see docs/api-reference.md); `scope` is the filter set the number was computed with. The list total equals that number.
export const INFLUENCE_DRILL_SORTS = { amount: 'amount', deal_amount: 'deal_amount', created_date: 'created', close_date: 'close', first_touch: 'first_touch' };
const fmtCalendarDate = (v) => (v ? formatShortDate(v) : '—');
const tagNames = (tags) => (tags || []).map((t) => t.name).join('; ');

export const influenceDrillColumns = (amountKind, amountLabel = 'Influenced $') => [
  dealNameColumn({ id: 'oppname',      label: 'Opportunity', width: 230 }),
  { id: 'account_name', label: 'Account',     width: 150 },
  { id: 'amount',       label: amountLabel,   width: 120, align: 'right', format: fmtCurrency,
    tooltip: amountKind === 'lines'
      ? 'The deal lines the influenced number counted (SKU tag matched to the attended workshop or event). The list adds up to the number you clicked.'
      : 'Whole deal amount. The list adds up to the number you clicked.' },
  ...(amountKind === 'lines' ? [{ id: 'deal_amount', label: 'Deal $', width: 100, align: 'right', format: fmtCurrency, tooltip: 'Full Salesforce opportunity amount' }] : []),
  { id: 'stage_name',   label: 'Stage',       width: 130, chipType: 'stage' },
  { id: 'created_date', label: 'Created',     width: 96,  format: fmtCalendarDate },
  { id: 'first_touch',  label: 'First touch', width: 96,  format: fmtCalendarDate, tooltip: 'First attendance by an SE or technical team member or contact on the deal' },
  { id: 'owner_name',   label: 'SF Owner',    width: 140 },
  ...(amountKind === 'lines' ? [{ id: 'tags', label: 'Credited products', width: 170, noWrap: false, format: tagNames,
    tooltip: 'Product tags of the credited lines' }] : []),
  workshopCountColumn({ id: 'workshops', label: 'Workshops' }),
];

// The API names the whole-deal vs matched-line figure in `amount_kind`; the columns follow it, so the config asks for it up front.
export const influenceAmountKind = (of) => (/^(tier:deep_dive|tag:|category:|workshop:|topic:)/.test(of) ? 'lines' : 'deal');

export const makeInfluenceDrillConfig = (of, scope = {}, { state = 'all', amountKind = influenceAmountKind(of), amountLabel } = {}) => ({
  endpoint: '/exec/influence/deals',
  scopeKey: JSON.stringify([of, state, scope]),
  buildParams: (base) => {
    const p = new URLSearchParams();
    p.set('of', of);
    const start = base.startDate || scope.startDate;
    const end = base.endDate || scope.endDate;
    if (start && end) { p.set('startDate', start); p.set('endDate', end); } else if (scope.window === 'all') p.set('window', 'all');
    if (scope.segment && scope.segment !== 'all') p.set('segment', scope.segment);
    if (scope.tecProgramTagId) p.set('tecProgramTagId', String(scope.tecProgramTagId));
    if (scope.tagId) p.set('tagId', String(scope.tagId));
    if (scope.geography) p.set('geo', scope.geography);
    if (scope.eventId) p.set('event_id', String(scope.eventId));
    if (scope.threshold) p.set('threshold', String(scope.threshold));
    if (scope.surface) p.set('surface', scope.surface);
    if (state && state !== 'all') p.set('state', state);
    const size = Math.min(base.pageSize ?? 25, 200);
    p.set('limit', String(size));
    p.set('page', String((base.page ?? 0) + 1));
    if (base.generation) p.set('generation', base.generation);
    p.set('sort', INFLUENCE_DRILL_SORTS[base.sortBy] || 'amount');
    p.set('order', base.sortOrder === 'asc' ? 'asc' : 'desc');
    return p;
  },
  normalize: (d) => {
    if (d.available === false) throw new Error(d.message || `not available (${d.reason || 'unknown'})`);
    return {
      total: d.total.n_deals,
      total_amount: d.total.amount,
      rows: d.rows.map((r) => ({
        ...r,
        workshops: r.workshops.map((w) => ({ id: w.kind === 'workshop' ? w.key : null, name: w.name || (w.kind === 'event' ? `Event ${String(w.key).slice(1)}` : `Workshop ${w.key}`) })),
      })),
    };
  },
  columns: influenceDrillColumns(amountKind, amountLabel),
  totalKey: 'amount',
  idKey: 'oppid',
  panelType: 'opportunities',
  maxPageSize: 200,
  noNewTab: true,
  disclaimer: 'The list is exactly the deals behind the number you clicked: same period, filters and credit rule, as of the last nightly run. The total equals that number.',
});

// ── Plan 2213 conversions A: the Participants and Workshops tiles with the service on ──────────────────────────────────
// `/exec/workshops-ranked` then answers the influenced Workshop Index (all audiences, tag-matched credit, rows overlap) and `/exec/employees`
// the same people without per-person money (an influenced deal is credited to the attendance of the people on the deal, not to one person).
// `/exec/influence/status` lists the converted routes in `influence_conversions`; `convertDrill` swaps the old config for them.
const WORKSHOPS_INFLUENCED_COLUMNS = [
  WORKSHOP_COLUMNS[0],
  { id: 'participants', label: 'Attendees', width: 90, align: 'right' },
  { id: 'n_deals', label: 'Deals', width: 70, align: 'right', tooltip: 'TEC-influenced deals credited to this workshop' },
  { id: 'pipeline', label: 'Influenced pipeline $', width: 140, align: 'right', format: fmtCurrency,
    tooltip: 'Deal lines whose product tag the workshop carries, on influenced deals created in the period; all stages' },
  { id: 'won', label: 'Won $', width: 110, align: 'right', format: fmtCurrency },
  WORKSHOP_COLUMNS[5],
];
// Plan 2217 stream B: the per-person money columns are replaced by a count of the influenced deals on the person's account(s) and a link
// to them; a person with no account link says so (never a zero).
const PEOPLE_INFLUENCED_COLUMNS = [
  ...PARTICIPANT_COLUMNS.filter((c) => c.id !== 'pipeline' && c.id !== 'won'),
  { id: 'deals_label', label: 'Influenced deals', width: 150, align: 'right', sortable: false,
    tooltip: "Influenced deals created in the period on the account(s) this person belongs to (their opportunity-team or contact role, a confirmed email-domain match, or their Salesforce contact). Not a share of the person's own credit: every attendee of the account sees the same deals. \"M credited directly\" counts the deals where this person is on the deal's team or contacts and attended TEC by the deal's end date.",
    renderCell: (v, row, ctx) => <PersonDealsCell row={row} ctx={ctx} /> },
];

export const WORKSHOP_INFLUENCED_CONFIG = {
  endpoint: '/exec/workshops-ranked',
  buildParams: (base) => {
    const p = new URLSearchParams();
    if (base.startDate) p.set('startDate', base.startDate);
    if (base.endDate)   p.set('endDate', base.endDate);
    p.set('basis', 'influenced');
    return p;
  },
  normalize: (d) => ({ rows: throwIfUnavailable(d) }),
  columns: WORKSHOPS_INFLUENCED_COLUMNS,
  totalKey: 'pipeline',
  idKey: 'name',
  panelType: 'workshops',
  noNewTab: true,
  disclaimer: 'TEC-influenced deals created in the period, credited to each workshop by the deal lines whose product tag it carries. A deal shared by several workshops counts in full in each, so rows do not sum. Covers every audience. Reach, not revenue.',
};

export const influencedPeopleConfig = (config) => ({
  ...config,
  buildParams: (base, filters) => { const p = config.buildParams(base, filters); p.set('basis', 'influenced'); return p; },
  normalize: (d) => { throwIfUnavailable(d); return d && Array.isArray(d.rows) ? { ...d, rows: d.rows.map((r) => ({ ...r, deals_label: dealsLabel(r) })) } : d; },
  columns: PEOPLE_INFLUENCED_COLUMNS,
  noNewTab: true,
  amountNA: true,
  coverageLine: (rows) => personDealsCoverage(rows),
  coverageTestId: 'person-deals-coverage',
  disclaimer: `People who attended in the period. ${PERSON_DEALS_LABEL}`,
});

const PEOPLE_CONFIGS = [PARTICIPANT_CONFIG, PARTICIPANT_INTERNAL_CONFIG, PARTICIPANT_EXTERNAL_CONFIG, PARTICIPANT_CHANNEL_CONFIG, PARTICIPANT_CUSTOMER_CONFIG];
const WORKSHOP_CONFIGS = [WORKSHOP_CONFIG, WORKSHOP_INTERNAL_CONFIG, WORKSHOP_EXTERNAL_CONFIG];
const PEOPLE_INFLUENCED = new Map(PEOPLE_CONFIGS.map((c) => [c, influencedPeopleConfig(c)]));

const FILTERED_OPP_INFLUENCED = new Map(); // stable identity per (tag, stage, audience): the panel does not refetch on re-render
const influencedFilteredOpp = (args) => {
  const k = JSON.stringify(args);
  if (!FILTERED_OPP_INFLUENCED.has(k)) FILTERED_OPP_INFLUENCED.set(k, makeFilteredOppConfig(...args, true));
  return FILTERED_OPP_INFLUENCED.get(k);
};

export const convertDrill = (config, status) => {
  const on = status?.influence_conversions || [];
  if (on.includes('employees') && PEOPLE_INFLUENCED.has(config)) return PEOPLE_INFLUENCED.get(config);
  if (on.includes('workshops_ranked') && WORKSHOP_CONFIGS.includes(config)) return WORKSHOP_INFLUENCED_CONFIG;
  if (on.includes('filtered_opportunities') && config?.filteredOpp && !config.filteredOpp[2]) return influencedFilteredOpp(config.filteredOpp);
  return config;
};
